import { useState } from 'react';
import { Phone, MessageSquare, Mail, X, Copy, Check, ExternalLink } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';

/**
 * ContactActionButtons - Shared component for Call/Text/Email actions
 * Implements: Call chooser modal, Text templates, proper mobile/desktop handling
 */
export function ContactActionButtons({ 
  variant = 'default', // 'default' | 'compact' | 'hero'
  showWhatsApp = true,
  className = ''
}) {
  const { language } = useLanguage();
  const [activeModal, setActiveModal] = useState(null); // 'call' | 'text' | null
  const [copied, setCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(0);

  const phone = siteConfig.contact.phone;
  const phoneClean = siteConfig.contact.phoneClean;
  const email = siteConfig.brand.email;
  const whatsappLink = siteConfig.contact.whatsappLink;

  const labels = {
    en: {
      call: 'Call',
      text: 'Text',
      email: 'Email',
      whatsapp: 'WhatsApp',
      callFromPhone: 'Call from your phone',
      callFromComputer: 'Call from computer',
      copyNumber: 'Copy number',
      useCallingApp: 'Use your calling app (FaceTime, Google Voice)',
      textTemplates: 'Quick Message Templates',
      copyTemplate: 'Copy & Send',
      openMessages: 'Open Messages',
      template1: 'Checking availability for [Day] at [Time]',
      template2: "What's included in [Package]?",
      template3: "Service area question: I'm near [Area]",
      copied: 'Copied!',
    },
    es: {
      call: 'Llamar',
      text: 'Mensaje',
      email: 'Email',
      whatsapp: 'WhatsApp',
      callFromPhone: 'Llamar desde tu teléfono',
      callFromComputer: 'Llamar desde computadora',
      copyNumber: 'Copiar número',
      useCallingApp: 'Usa tu app de llamadas (FaceTime, Google Voice)',
      textTemplates: 'Plantillas Rápidas',
      copyTemplate: 'Copiar y Enviar',
      openMessages: 'Abrir Mensajes',
      template1: 'Verificando disponibilidad para [Día] a las [Hora]',
      template2: '¿Qué incluye el paquete [Paquete]?',
      template3: 'Pregunta sobre área de servicio: Estoy cerca de [Área]',
      copied: '¡Copiado!',
    }
  };

  const t = labels[language];

  const templates = [
    t.template1,
    t.template2,
    t.template3,
  ];

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCallClick = (e) => {
    e.preventDefault();
    api.trackEvent('cta_click', 'contact_call', window.location.pathname);
    
    // On mobile, just use tel: link directly
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.location.href = `tel:${phone}`;
    } else {
      setActiveModal('call');
    }
  };

  const handleTextClick = (e) => {
    e.preventDefault();
    api.trackEvent('cta_click', 'contact_text', window.location.pathname);
    
    // On mobile, open sms: directly
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.location.href = `sms:${phone}`;
    } else {
      setActiveModal('text');
    }
  };

  const handleEmailClick = () => {
    api.trackEvent('cta_click', 'contact_email', window.location.pathname);
  };

  const buttonClass = variant === 'hero' 
    ? 'btn-neon'
    : variant === 'compact'
    ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all'
    : 'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all';

  return (
    <>
      <div className={`flex flex-wrap items-center gap-3 ${className}`} data-testid="contact-action-buttons">
        {/* Call Button */}
        <button
          onClick={handleCallClick}
          className={`${buttonClass} ${variant === 'hero' ? 'btn-neon-pink' : 'bg-[#27D7FF]/10 text-[#27D7FF] border border-[#27D7FF]/30 hover:bg-[#27D7FF]/20'}`}
          data-testid="contact-call-btn"
        >
          <Phone className="w-4 h-4" />
          <span>{t.call}</span>
        </button>

        {/* Text Button with angry face per spec */}
        <button
          onClick={handleTextClick}
          className={`${buttonClass} ${variant === 'hero' ? 'btn-neon-outline' : 'bg-[#FF4DA6]/10 text-[#FF4DA6] border border-[#FF4DA6]/30 hover:bg-[#FF4DA6]/20'}`}
          data-testid="contact-text-btn"
        >
          <MessageSquare className="w-4 h-4" />
          <span>{t.text}</span>
        </button>

        {/* WhatsApp Button */}
        {showWhatsApp && (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => api.trackEvent('cta_click', 'contact_whatsapp', window.location.pathname)}
            className={`${buttonClass} ${variant === 'hero' ? 'btn-neon-cyan' : 'bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20'}`}
            data-testid="contact-whatsapp-btn"
          >
            <MessageSquare className="w-4 h-4" />
            <span>{t.whatsapp}</span>
          </a>
        )}

        {/* Email Button */}
        <a
          href={`mailto:${email}`}
          onClick={handleEmailClick}
          className={`${buttonClass} ${variant === 'hero' ? 'bg-[#111022] border border-[#8B5CFF]/50 text-[#8B5CFF] hover:bg-[#8B5CFF]/10' : 'bg-[#8B5CFF]/10 text-[#8B5CFF] border border-[#8B5CFF]/30 hover:bg-[#8B5CFF]/20'}`}
          data-testid="contact-email-btn"
        >
          <Mail className="w-4 h-4" />
          <span>{t.email}</span>
        </a>
      </div>

      {/* Call Chooser Modal */}
      {activeModal === 'call' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setActiveModal(null)}>
          <div className="bg-[#111022] rounded-2xl border border-white/10 p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-[#F4F2FF]">{t.call}</h3>
              <button onClick={() => setActiveModal(null)} className="text-[#A7A3BA] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <a
                href={`tel:${phone}`}
                className="flex items-center gap-3 w-full p-4 rounded-xl bg-[#27D7FF]/10 border border-[#27D7FF]/30 text-[#27D7FF] hover:bg-[#27D7FF]/20 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{t.callFromPhone}</span>
              </a>
              
              <button
                onClick={() => handleCopy(phone)}
                className="flex items-center gap-3 w-full p-4 rounded-xl bg-[#0B0A12] border border-white/10 text-[#A7A3BA] hover:border-white/20 transition-colors"
              >
                {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? t.copied : t.copyNumber}</span>
              </button>
              
              <p className="text-xs text-[#A7A3BA] text-center pt-2">
                {t.useCallingApp}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Text Templates Modal */}
      {activeModal === 'text' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setActiveModal(null)}>
          <div className="bg-[#111022] rounded-2xl border border-white/10 p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-[#F4F2FF]">{t.textTemplates}</h3>
              <button onClick={() => setActiveModal(null)} className="text-[#A7A3BA] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3 mb-6">
              {templates.map((template, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedTemplate(idx)}
                  className={`w-full p-3 rounded-xl text-left text-sm transition-all ${
                    selectedTemplate === idx
                      ? 'bg-[#FF4DA6]/20 border border-[#FF4DA6] text-[#F4F2FF]'
                      : 'bg-[#0B0A12] border border-white/10 text-[#A7A3BA] hover:border-white/20'
                  }`}
                >
                  {template}
                </button>
              ))}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => handleCopy(templates[selectedTemplate])}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#8B5CFF]/20 text-[#8B5CFF] border border-[#8B5CFF]/30 hover:bg-[#8B5CFF]/30 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? t.copied : t.copyTemplate}</span>
              </button>
              
              <a
                href={`sms:${phone}?body=${encodeURIComponent(templates[selectedTemplate])}`}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FF4DA6] text-white hover:shadow-lg hover:shadow-[#FF4DA6]/30 transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                <span>{t.openMessages}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ContactActionButtons;
